// A SZÁMLÁIM LISTA TÉTEL-PANELJE — a számla tételei és a számlára kerülő megjegyzés, a `invoiceDetail`
// végpontról (a lista-sor nem hordozza a tételeket). A közös-számla jelölést a LISTA-SOR adja (az már a
// fiók-scope-pal szűkített kérésből jött), a panel csak a tételeket és a megjegyzést tölti.
import type { PortalInvoiceRow } from '@atlas/contracts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalInvoiceItems } from '@/components/portal/portal-invoice-items';
import { PortalSharedBadge } from '@/components/portal/portal-shared-badge';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { portalKeys } from '@/features/portal/api';
import { api } from '@/lib/api';

export function PortalInvoiceDetailSheet({
  invoice,
  onClose,
}: {
  /** A megnyitott lista-sor; `null` → zárva. */
  invoice: PortalInvoiceRow | null;
  onClose: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'hu';
  const id = invoice?.id ?? null;
  const q = useQuery({
    queryKey: portalKeys.invoiceDetail(id ?? '', lang),
    enabled: id !== null,
    queryFn: () => api.portal.invoiceDetail.query({ params: { id: id ?? '' }, query: { lang } }),
  });
  const detail = q.data?.status === 200 ? q.data.body : undefined;
  const failed = q.data !== undefined && q.data.status !== 200;

  return (
    <Sheet open={invoice !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0">
        <div className="relative shrink-0 p-4 pr-12">
          <SheetTitle>
            {t('portal.billing.invoice.sheetTitle')}
            {invoice ? ` — ${invoice.invoiceNumber}` : ''}
          </SheetTitle>
          <span
            aria-hidden
            className="absolute inset-x-4 bottom-0 h-px bg-[color:var(--table-hairline)]"
          />
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <PortalSharedBadge shared={invoice?.shared ?? null} />
          {failed || q.isError ? (
            <PortalErrorState onRetry={() => void q.refetch()} />
          ) : detail === undefined ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <PortalInvoiceItems invoice={detail} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
